import _, {filter, mapValues} from 'lodash';

import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  buildConversationTimelineBoundary,
  isWithinConversationTimelineRange,
} from '../../../../helpers/viewerHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  MessageActivityModel,
  OtherActivityTypesEnum,
} from '../../../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../../../models/api/activitySourceModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {collapseById} from '../../../../models/api/dataGroupModel';
import {
  MessageModel,
  MessageVisualStatusesEnum,
  MessageVisualTypesEnum,
} from '../../../../models/api/messageModel';
import {AppAction} from '../../../appActions';
import {ResourceMap} from '../../../appTypes';
import {DataCascade} from '../../../data/dataCascade';
import {DataGroupsActionsEnum} from '../../../data/dataGroupsActions';
import {
  findParametersActivityDate,
  findParametersActivityId,
} from '../../inboxes/parameters/inboxesParametersHelpers';
import {ViewerPanelPageActionsEnum} from '../viewerPanelActions';
import {ViewerPanelConversationPageState} from '../viewerPanelState';
import {ActivityViewerActionsEnum, isActivityViewerAction} from './activityViewerActions';
import {ActivityViewerState} from './activityViewerState';

/*
 * Activity viewers.
 */

export function activityViewersReducer(
  page: ViewerPanelConversationPageState,
  state: ResourceMap<ActivityViewerState>,
  action: AppAction,
  {data, updatedResources}: DataCascade,
): ResourceMap<ActivityViewerState> {
  // If this is an activity action, update the targeted activity.
  if (isActivityViewerAction(action)) {
    const {activityId} = action.payload;
    const activityState = state[activityId];
    const newActivityState = activityViewerReducer(activityState, action);

    if (activityState === newActivityState) {
      return state;
    }

    return {
      ...state,
      [activityId]: newActivityState,
    };
  }

  // Single conversation fetch/fetchMore.
  const {parameters, conversationId, shouldExpandMessages} = page;
  const aroundActivityId = findParametersActivityId(parameters);
  const aroundActivityDate = findParametersActivityDate(parameters);

  switch (action.type) {
    case ViewerPanelPageActionsEnum.FETCH_CONVERSATION_COMPLETED: {
      const {activityIds} = action.payload;
      const conversation = data.conversations.byId[conversationId];
      const updatedActivities = _(activityIds)
        .map((id) => data.activities[id])
        .compact()
        .value();

      return updateStateFromDataGroup(conversationId, state, action, updatedActivities, data.messages, {
        aroundActivityId,
        aroundActivityDate,
        conversation,
        shouldExpandMessages,
      });
    }

    case ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION_COMPLETED: {
      const {activityIds} = action.payload;
      const conversation = data.conversations.byId[conversationId];
      const updatedActivities = _(activityIds)
        .map((id) => data.activities[id])
        .compact()
        .value();

      return updateStateFromDataGroup(conversationId, state, action, updatedActivities, data.messages, {
        conversation,
        shouldExpandMessages,
      });
    }

    case ViewerPanelPageActionsEnum.ALL_MESSAGES_COLLAPSED:
      return mapValues(state, (a) => a && activityViewerReducer(state[a.activityId], action));

    default: {
      if (action.type === DataGroupsActionsEnum.DATA_UPDATED && action.payload.shouldNotCascade) {
        return state;
      }

      // Only reduce updated activities that are in the bounds of the page.
      const updatedActivities = updatedResources.activities?.filter((activity) => {
        const boundary = buildConversationTimelineBoundary(activity);
        return isWithinConversationTimelineRange(page, boundary);
      });

      return updateStateFromDataGroup(conversationId, state, action, updatedActivities || [], data.messages);
    }
  }
}

interface UpdateStateFromDataGroupOptions {
  aroundActivityId?: number;
  aroundActivityDate?: number;
  conversation?: ConversationModel;
  shouldExpandMessages?: boolean;
}

function updateStateFromDataGroup(
  conversationId: number,
  state: ResourceMap<ActivityViewerState>,
  action: AppAction,
  updatedActivities: ReadonlyArray<ActivityModel>,
  allMessages: ResourceMap<MessageModel>,
  options: UpdateStateFromDataGroupOptions = {},
) {
  const activities = updatedActivities;
  if (activities.length === 0) {
    return state;
  }

  // Check if we need to update anything.
  const timelineActivities = filter(
    collapseById(activities),
    (a) => a.conversationId === conversationId && a.type !== OtherActivityTypesEnum.OTHER,
  );
  if (timelineActivities.length === 0) {
    return state;
  }

  // Make a map of the messages for quick access.
  const messagesMap = allMessages;
  const statePatch = _(timelineActivities)
    .keyBy((a) => a.id)
    .mapValues((activity) => {
      // Check if the content for this activity is already loaded.
      const messageUid = activity.visualType === ActivityVisualTypesEnum.MESSAGE && activity.messageUid;
      const message = messageUid ? messagesMap[messageUid] : undefined;

      return activityViewerReducer(state[activity.id], action, {
        ...options,
        activity,
        message,
      });
    })
    .value();

  return {...state, ...statePatch};
}

/*
 * Activity viewer.
 */

interface ActivityViewerReducerOptions extends UpdateStateFromDataGroupOptions {
  activity?: ActivityModel;
  message?: MessageModel;
}

export function activityViewerReducer(
  state: ActivityViewerState | undefined,
  action: AppAction,
  options: ActivityViewerReducerOptions = {},
): ActivityViewerState | undefined {
  const {activity} = options;

  if (!state && activity) {
    return makeActivityViewerState(activity, options);
  }

  // This may happen if an action was dispatched after the activity viewer has been removed.
  if (!state) {
    return undefined;
  }

  // Modify an existing state
  switch (action.type) {
    case ViewerPanelPageActionsEnum.ALL_MESSAGES_COLLAPSED: {
      // This only applies to message activities.
      if (state.type !== ActivityVisualTypesEnum.MESSAGE) {
        return state;
      }

      return {
        ...state,
        isExpanded: false,
      };
    }

    case ActivityViewerActionsEnum.EXPANDED: {
      // This only applies to message and basic activities.
      if (state.type !== ActivityVisualTypesEnum.MESSAGE && state.type !== ActivityVisualTypesEnum.BASIC) {
        return state;
      }

      return {
        ...state,
        isExpanded: true,
      };
    }

    case ActivityViewerActionsEnum.COLLAPSED: {
      // This only applies to message and basic activities.
      if (state.type !== ActivityVisualTypesEnum.MESSAGE && state.type !== ActivityVisualTypesEnum.BASIC) {
        return state;
      }

      return {
        ...state,
        isExpanded: false,
      };
    }

    default:
      return state;
  }
}

function makeActivityViewerState(
  activity: ActivityModel,
  options: ActivityViewerReducerOptions = {},
): ActivityViewerState {
  switch (activity.visualType) {
    case ActivityVisualTypesEnum.MESSAGE:
      return {
        type: ActivityVisualTypesEnum.MESSAGE,
        activityId: activity.id,
        isExpanded: isMessageExpanded(activity, options),
        hasFailedToLoad: false,
        isExternalContentHidden: false,
      };

    case ActivityVisualTypesEnum.COMMENT:
      return {
        type: ActivityVisualTypesEnum.COMMENT,
        activityId: activity.id,
      };

    default:
      return {type: ActivityVisualTypesEnum.BASIC, activityId: activity.id, isExpanded: false};
  }
}

function isMessageExpanded(activity: MessageActivityModel, options: ActivityViewerReducerOptions = {}) {
  const {message, conversation, aroundActivityId, aroundActivityDate, shouldExpandMessages} = options;

  // Not a message or not an email, expand.
  if (!message || message.visualType !== MessageVisualTypesEnum.EMAIL) {
    return true;
  }

  // Partial message, we can't expand.
  if (message.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
    return false;
  }

  // Auto-responses are collapsed initially.
  const isAutoResponseMessage =
    activity.visualType === ActivityVisualTypesEnum.MESSAGE &&
    (activity.source?.visualType === ActivitySourceVisualTypesEnum.OUT_OF_OFFICE_REPLY ||
      activity.source?.visualType === ActivitySourceVisualTypesEnum.AUTOMATED_REPLY);
  if (isAutoResponseMessage) {
    return false;
  }

  // "Expand all" requested, expand.
  if (shouldExpandMessages) {
    return true;
  }

  // Focused activity specified, expand if this is it.
  if (aroundActivityId && activity.id === aroundActivityId) {
    return true;
  }

  // If we are focusing around a specific date, expand that date and everything newer. (Or if we are starting from the
  // top, expand all.)
  if (isDefined(aroundActivityDate) && activity.date >= aroundActivityDate) {
    return true;
  }

  // No conversation, err on the side of caution and expand.
  if (!conversation) {
    return true;
  }

  // If missing last manual message information, err on the side of caution and expand.
  const {lastManualMessage} = conversation;
  if (!lastManualMessage) {
    return true;
  }

  // Last message, expand.
  if (lastManualMessage.uid === activity.messageUid) {
    return true;
  }

  // Expand the activity if it's the "true" last manual message of the conversation.
  // * In some cases, `conversation.lastManualMessage` isn't actually the last manual message in P3,
  // so we have this workaround.
  if (lastManualMessage.uid !== activity.messageUid && activity.date > lastManualMessage.sentAt) {
    return true;
  }

  // Unread message, expand.
  if (!conversation.seenAt || activity.date > conversation.seenAt) {
    return true;
  }

  // Otherwise, keep collapsed.
  return false;
}
